/**
 * @fileoverview The official social brand marks marketing-shell serves as files (LIFE-SHORTLISTED-001 U33):
 * LinkedIn's [in] mark, which LinkedIn publishes as PNG artwork only (no SVG, no path).
 *
 * VENDORED, never drawn, recoloured or traced: the two files under `assets/brand-marks/linkedin/` are
 * byte-identical copies of `InBug-White.png` and `InBug-Black.png` (840×779, the ® part of the artwork)
 * from LinkedIn's official download,
 * `https://content.linkedin.com/content/dam/me/business/en-us/amp/xbu/linkedin-revised-brand-guidelines/logos/in-logo.zip`
 * (47,771 bytes, sha256 `32c4b7bbf902c1d957881e2eca16bc765891dc86da8ce4d416a849f161222a87`), fetched
 * 2026-10-21. Owner rulings Q6 ("Add it") and Q-D2-U33-MARK ("Black or white by contrast"); the terms
 * read that day are quoted in `assets/brand-marks/linkedin/PROVENANCE.md`.
 *
 * A browser cannot read a package's files, so each served app carries a copy at
 * `public/assets/brand-marks/<publicName>`, placed by `bin/sync-brand-marks.mjs` (the brand-assets
 * `sync-colony.mjs` precedent) and held byte for byte by the cross-app `socialBrandMarkSync` contract.
 * This module is the ONE list of those files and their pins: `<SocialIcon>`'s policy names its sources
 * from it, the sync copies what it lists, and a re-vendor is a deliberate edit of every pin.
 *
 * @module @puffin-core/marketing-shell/social/brandMarks
 */

/** Where the vendored files live, relative to the marketing-shell package root. */
export const BRAND_MARK_VENDORED_DIRECTORY = 'assets/brand-marks';

/**
 * The URL directory every serving app answers the marks at (`public/assets/brand-marks/`). Under
 * `/assets/` because that is the ONE directory a vanity host serves files from: purchase-web's tenant-site
 * server refuses `/branding/*` and every other image (the cross-app `vanityHostServesOnlyTenantSite`
 * contract resolves each mark's URL against it), and the build copies `public/assets/` into the bundle
 * directory, which every marketing site serves too.
 */
export const BRAND_MARK_PUBLIC_DIRECTORY = '/assets/brand-marks';

/** Where LinkedIn's files came from, pinned. */
export const LINKEDIN_MARK_SOURCE = Object.freeze({
  url: 'https://content.linkedin.com/content/dam/me/business/en-us/amp/xbu/linkedin-revised-brand-guidelines/logos/in-logo.zip',
  zipSha256: '32c4b7bbf902c1d957881e2eca16bc765891dc86da8ce4d416a849f161222a87',
  fetchedOn: '2026-10-21',
});

/**
 * Every vendored mark: its file under {@link BRAND_MARK_VENDORED_DIRECTORY}, the name it is served
 * under in {@link BRAND_MARK_PUBLIC_DIRECTORY}, and its sha256.
 *
 * @type {ReadonlyArray<Readonly<{vendored: string, publicName: string, sha256: string}>>}
 */
export const BRAND_MARK_FILES = Object.freeze([
  Object.freeze({
    vendored: 'linkedin/InBug-White.png',
    publicName: 'linkedin-inbug-white.png',
    sha256: 'ed22a5ab46c171e3449708ff34923de7298da00ac204c7f85444db05bf888791',
  }),
  Object.freeze({
    vendored: 'linkedin/InBug-Black.png',
    publicName: 'linkedin-inbug-black.png',
    sha256: 'b80ab65537aad1b5013da98fe82d1c36c1bb4ca896fe7829a880cc5f307693ca',
  }),
]);

/**
 * @param {string} vendored - a {@link BRAND_MARK_FILES} entry's `vendored` path
 * @returns {string} the absolute URL path every serving app answers that file at
 */
export function brandMarkSrc(vendored) {
  const file = BRAND_MARK_FILES.find((entry) => entry.vendored === vendored);
  if (!file) {
    throw new Error(`brandMarkSrc: no vendored brand mark "${vendored}"`);
  }
  return `${BRAND_MARK_PUBLIC_DIRECTORY}/${file.publicName}`;
}
